/**
 * Block 1 (#1892) — the global brake, rendered loudly.
 *
 * ── WHY THIS IS A BANNER AND NOT A TOGGLE IN A CORNER ───────────────────────
 * Migration `0031` seeds the switch CLOSED, and that is the mechanical gate
 * that stops the production promote from producing a first mail. The direct
 * consequence is that "off" is the ORDINARY state of a freshly promoted
 * environment rather than an incident — so the failure mode this default
 * creates is not a wrong setting, it is a correct setting nobody noticed:
 * everyone assumes mail is going out and none is. A grey pill would be that
 * failure mode wearing a UI.
 *
 * So the state is a full-width block with its own copy, and the two closed
 * states say DIFFERENT things: nobody has ever opened it (seeded default, no
 * customer has ever been mailed from here) versus somebody closed it (there is
 * a person, an instant and usually a reason, and those are the story).
 *
 * ── WHY OPENING IT IS CONFIRMED ─────────────────────────────────────────────
 * Pressing the switch to `on` is the press that lets the next pass mail real
 * customers about their own money. It is not undoable after the pass runs, so
 * the dialog leads with the consequence and the reassurance comes second.
 */
import { useEffect, useState } from 'react';
import type { AccountBalanceAlertSettings } from '@nadoutong/contracts';
import { useOpsUserLabel } from '../../api/directory.ts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, EmptyState, FormField, Input, Panel, Skeleton, Switch } from '../../ui/index.ts';
import { readSwitchState, switchStateLabel, switchStateTone } from './balanceAlertStatus.ts';
import { BalanceAlertSwitchDialog } from './BalanceAlertSwitchDialog.tsx';

interface Props {
  readonly settings: AccountBalanceAlertSettings | null;
  readonly loading: boolean;
  readonly error: boolean;
  readonly saving: boolean;
  readonly onRetry: () => void;
  readonly onSubmit: (next: { readonly enabled: boolean; readonly reason: string | null }) => void;
}

export function BalanceAlertSwitchPanel({
  settings,
  loading,
  error,
  saving,
  onRetry,
  onSubmit,
}: Props): JSX.Element {
  const t = useT();
  const [pending, setPending] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (pending === null) setReason('');
  }, [pending]);

  if (loading) {
    return (
      <Panel caption={t('balance_alerts.switch.caption')} padded testId="balance-alerts-switch-loading">
        <Skeleton className="h-20 w-full" />
      </Panel>
    );
  }

  // A failed read must NOT fall through to a rendered "off". Every closed
  // state on this page is a claim that no mail is going out, and an unread
  // switch is not evidence for that claim in either direction.
  if (error || settings === null) {
    return (
      <Panel caption={t('balance_alerts.switch.caption')} padded testId="balance-alerts-switch-error">
        <EmptyState
          title={t('balance_alerts.switch.error')}
          hint={t('balance_alerts.switch.error_hint')}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      </Panel>
    );
  }

  const state = readSwitchState(settings);

  return (
    <Panel
      caption={t('balance_alerts.switch.caption')}
      padded
      testId="balance-alerts-switch-panel"
      actions={
        <Chip tone={switchStateTone(state)} testId="balance-alerts-switch-chip">
          {t(switchStateLabel(state))}
        </Chip>
      }
    >
      <div className="flex flex-col gap-3">
        <StateBanner state={state} settings={settings} />

        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={settings.enabled}
            disabled={saving}
            onCheckedChange={(next) => setPending(next)}
            label={t('balance_alerts.switch.toggle_label')}
            testId="balance-alerts-switch"
          />
          <p className="m-0 text-dense text-paper-low">{t('balance_alerts.switch.toggle_hint')}</p>
        </div>
      </div>

      <BalanceAlertSwitchDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        enabling={pending === true}
        loading={saving}
        reasonField={
          <FormField
            label={t('balance_alerts.switch.reason_label')}
            fieldId="balance-alerts-switch-reason"
            hint={t('balance_alerts.switch.reason_hint')}
          >
            <Input
              id="balance-alerts-switch-reason"
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              data-testid="balance-alerts-switch-reason"
            />
          </FormField>
        }
        onConfirm={() => {
          if (pending === null) return;
          onSubmit({ enabled: pending, reason: reason.trim().length > 0 ? reason.trim() : null });
          setPending(null);
        }}
      />
    </Panel>
  );
}

/**
 * The sentence an operator reads first.
 *
 * Each of the three states gets its OWN copy rather than one template with a
 * substituted word, because they call for different actions: open it, look at
 * why it was closed, or nothing at all.
 */
function StateBanner({
  state,
  settings,
}: {
  readonly state: ReturnType<typeof readSwitchState>;
  readonly settings: AccountBalanceAlertSettings;
}): JSX.Element {
  const t = useT();
  const opsUserLabel = useOpsUserLabel();
  const tone =
    state === 'open'
      ? 'border-ok-line bg-ok-dim/50 text-ok'
      : state === 'closed'
        ? 'border-bad-line bg-bad-dim/50 text-bad-bright'
        : 'border-warn-line bg-warn-dim/50 text-warn';

  return (
    <div className={`flex flex-col gap-1.5 rounded border px-3 py-2.5 ${tone}`} data-testid="balance-alerts-switch-banner">
      <p className="m-0 text-body font-medium" data-testid="balance-alerts-switch-headline">
        {t(
          state === 'open'
            ? 'balance_alerts.switch.headline_open'
            : state === 'closed'
              ? 'balance_alerts.switch.headline_closed'
              : 'balance_alerts.switch.headline_never_opened',
        )}
      </p>
      <p className="m-0 text-dense text-paper-mid" data-testid="balance-alerts-switch-attribution">
        {settings.updated_at === null
          ? t('balance_alerts.switch.attribution_never')
          : t('balance_alerts.switch.attribution', {
              who: opsUserLabel(settings.updated_by_ops_user_id) || t('balance_alerts.switch.actor_unknown'),
              when: formatLocalTimestamp(settings.updated_at),
            })}
      </p>
      {settings.reason !== null && settings.reason.length > 0 ? (
        <p className="m-0 text-dense text-paper-mid" data-testid="balance-alerts-switch-reason-shown">
          {t('balance_alerts.switch.reason_shown', { reason: settings.reason })}
        </p>
      ) : null}
    </div>
  );
}
